import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function TutorialOverlay({ onClose }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect if user is on mobile device
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|blackberry|iphone|ipad|ipod|opera mini|iemobile|wpdesktop/i.test(userAgent.toLowerCase());
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      
      return isMobileDevice || (isTouchDevice && isSmallScreen);
    };

    setIsMobile(checkIfMobile());

    // Listen for window resize to update mobile detection
    const handleResize = () => {
      setIsMobile(checkIfMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    // Mark tutorial as completed in localStorage
    localStorage.setItem('gallery-tutorial-completed', 'true');
    onClose();
  };

  const steps = [
    {
      title: t('welcomeToGallery'),
      content: (
        <div className="tutorial-welcome">
          <p>{t('tutorialEnjoy')}</p>
        </div>
      )
    },
    {
      title: t('tutorialCameraControlTitle'),
      content: (
        <div className="tutorial-navigation">
          <p>
            {isMobile ? t('tutorialCameraControlMobile') : t('tutorialCameraControlDesktop')}
          </p>
          <div className="tutorial-demo">
            {isMobile ? (
              <div className="phone-demo">
                <div className="phone-frame">
                  <div className="phone-screen"></div>
                </div>
                <div className="finger-pointer"></div>
              </div>
            ) : (
              <div className="mouse-demo">
                <div className="drag-arrows">
                  <span>←</span>
                  <div className="mouse-icon">
                    <div className="mouse-body">
                      <div className="mouse-button"></div>
                    </div>
                  </div>
                  <span>→</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      title: t('tutorialMovementTitle'),
      content: (
        <div className="tutorial-movement">
          <p>
            {isMobile ? t('tutorialMovementMobile') : t('tutorialMovementDesktop')}
          </p>
          <div className="tutorial-demo">
            {isMobile ? (
              <div className="mobile-demo">
                <div className="joystick-demo">
                  <div className="joystick-base">
                    <div className="joystick-knob"></div>
                    <svg width="60" height="60" viewBox="0 0 60 60" style={{ position: 'absolute' }}>
                      <path d="M30 10 L25 17.5 L35 17.5 Z" fill="rgba(255,255,255,0.6)" />
                      <path d="M30 50 L25 42.5 L35 42.5 Z" fill="rgba(255,255,255,0.6)" />
                      <path d="M10 30 L17.5 25 L17.5 35 Z" fill="rgba(255,255,255,0.6)" />
                      <path d="M50 30 L42.5 25 L42.5 35 Z" fill="rgba(255,255,255,0.6)" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <div className="desktop-demo">
                <div className="keyboard-demo">
                  <div className="key">W</div>
                  <div className="key">A</div>
                  <div className="key">S</div>
                  <div className="key">D</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      title: t('tutorialInteractionTitle'),
      content: (
        <div className="tutorial-interaction">
          <p>{t('tutorialInteraction')}</p>
          <div className="tutorial-demo">
            <div className="artwork-demo">
              <div className="frame">
                <div className="artwork-content"></div>
                <div className="click-indicator">
                  <div className="click-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: t('tutorialMenuTitle'),
      content: (
        <div className="tutorial-menu">
          <p>{t('tutorialMenu')}</p>
          <div className="tutorial-demo">
            <div className="menu-demo">
              <div className="menu-button">
                <img 
                  src="/interactive-display-icon-186423-512.png" 
                  alt="Menu" 
                  className="menu-icon-img" 
                />
              </div>
              <div className="arrow-indicator">←</div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="tutorial-overlay">
      <div className="tutorial-backdrop" />
      <div className="tutorial-container">
        <div className="tutorial-header">
          <h2 className="tutorial-title">{steps[currentStep].title}</h2>
          <button className="tutorial-skip" onClick={handleSkip}>
            {t('tutorialSkip')}
          </button>
        </div>

        <div className="tutorial-content">
          {steps[currentStep].content}
        </div>

        <div className="tutorial-footer">
          <div className="tutorial-progress">
            <div className="progress-dots">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${index === currentStep ? 'active' : index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>
            <span className="progress-text">
              {currentStep + 1} / {steps.length}
            </span>
          </div>

          <div className="tutorial-actions">
            <button className="tutorial-button primary" onClick={handleNext}>
              {currentStep === steps.length - 1 ? t('tutorialGotIt') : t('tutorialContinue')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .tutorial-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          box-sizing: border-box;
        }

        .tutorial-backdrop {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(12px);
        }

        .tutorial-container {
          position: relative;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
          overflow: hidden;
        }

        .tutorial-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 16px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .tutorial-title {
          color: #1a1a1a;
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }

        .tutorial-skip {
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          font-size: 14px;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .tutorial-skip:hover {
          color: #1a1a1a;
          background: rgba(0, 0, 0, 0.05);
        }

        .tutorial-content {
          padding: 32px 24px;
          min-height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tutorial-welcome,
        .tutorial-navigation,
        .tutorial-movement,
        .tutorial-interaction,
        .tutorial-menu {
          text-align: center;
          color: #1a1a1a;
        }


        .tutorial-content p {
          font-size: 16px;
          line-height: 1.6;
          margin: 0 0 24px;
          color: #555;
        }

        .tutorial-demo {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 20px;
        }

        .phone-demo {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .phone-frame {
          width: 120px;
          height: 200px;
          border: 4px solid #333;
          border-radius: 16px;
          background: #1a1a1a;
          padding: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          position: relative;
        }

        .phone-screen {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 8px;
        }

        .finger-pointer {
          position: absolute;
          width: 24px;
          height: 24px;
          background: rgba(255, 255, 255, 0.9);
          border: 2px solid #4A90E2;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(74, 144, 226, 0.6);
          animation: fingerSwipe 3s ease-in-out infinite;
          z-index: 10;
        }

        .finger-pointer::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          background: #4A90E2;
          border-radius: 50%;
        }

        .mouse-demo {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mouse-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          animation: mouseDragMotion 2s ease-in-out infinite;
        }

        .mouse-body {
          width: 30px;
          height: 45px;
          border: 3px solid #333;
          border-radius: 15px;
          position: relative;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 8px;
        }

        .mouse-button {
          width: 8px;
          height: 12px;
          background: #4A90E2;
          border-radius: 4px;
          animation: mousePress 2s ease-in-out infinite;
        }

        .drag-arrows {
          display: flex;
          gap: 30px;
          font-size: 28px;
          color: #4A90E2;
          align-items: center;
        }

        .drag-arrows span {
          opacity: 0.6;
        }

        .mobile-demo {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .joystick-demo {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .joystick-base {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          border: 3px solid rgba(255, 255, 255, 0.4);
          background: rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .joystick-knob {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.6);
          animation: joystickMove 3s ease-in-out infinite;
        }

        .desktop-demo {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .keyboard-demo {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          width: fit-content;
        }

        .keyboard-demo .key:nth-child(1) {
          grid-column: 2;
        }

        .keyboard-demo .key:nth-child(2) {
          grid-column: 1;
          grid-row: 2;
        }

        .keyboard-demo .key:nth-child(3) {
          grid-column: 2;
          grid-row: 2;
        }

        .keyboard-demo .key:nth-child(4) {
          grid-column: 3;
          grid-row: 2;
        }

        .key {
          width: 40px;
          height: 40px;
          background: #333;
          border: 2px solid #4A90E2;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 16px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .floor-demo {
          width: 80px;
          height: 80px;
          background: linear-gradient(45deg, #444, #666);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .floor-tile {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px dashed rgba(74, 144, 226, 0.4);
          border-radius: 6px;
          position: relative;
        }

        .click-pulse {
          width: 12px;
          height: 12px;
          background: #4A90E2;
          border-radius: 50%;
          animation: pulse 1.5s infinite;
        }

        .artwork-demo {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .frame {
          width: 100px;
          height: 80px;
          background: linear-gradient(45deg, #8B4513, #A0522D);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.3);
          position: relative;
        }

        .artwork-content {
          position: absolute;
          top: 8px;
          left: 8px;
          right: 8px;
          bottom: 8px;
          background: linear-gradient(45deg, #4A90E2, #357ABD);
          border-radius: 2px;
        }

        .menu-demo {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .menu-button {
          width: 50px;
          height: 50px;
          background: white;
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          position: relative;
        }

        .menu-icon-img {
          width: 28px;
          height: 28px;
          object-fit: contain;
        }

        .arrow-indicator {
          font-size: 24px;
          color: #4A90E2;
          animation: bounce 2s infinite;
        }

        .click-indicator {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tutorial-footer {
          padding: 16px 24px 24px;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }

        .tutorial-progress {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .progress-dots {
          display: flex;
          gap: 8px;
        }

        .progress-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }

        .progress-dot.active {
          background: #1a1a1a;
          transform: scale(1.2);
        }

        .progress-dot.completed {
          background: #666;
        }

        .progress-text {
          color: #666;
          font-size: 14px;
        }

        .tutorial-actions {
          display: flex;
          justify-content: flex-end;
        }

        .tutorial-button {
          padding: 12px 24px;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tutorial-button.primary {
          background: #1a1a1a;
          color: white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .tutorial-button.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
          background: #333;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes wiggle {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes mouseDragMotion {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-15px);
          }
          50% {
            transform: translateX(0);
          }
          75% {
            transform: translateX(15px);
          }
        }

        @keyframes mouseClick {
          0% { 
            background: rgba(74, 144, 226, 0.1);
            transform: scale(1);
          }
          20% { 
            background: rgba(74, 144, 226, 0.6);
            transform: scale(0.95);
          }
          80% { 
            background: rgba(74, 144, 226, 0.6);
            transform: scale(0.95);
          }
          100% { 
            background: rgba(74, 144, 226, 0.1);
            transform: scale(1);
          }
        }

        @keyframes joystickMove {
          0% {
            transform: translate(0, 0);
          }
          25% {
            transform: translate(-10px, 0);
          }
          50% {
            transform: translate(0, -10px);
          }
          75% {
            transform: translate(10px, 0);
          }
          100% {
            transform: translate(0, 0);
          }
        }

        @keyframes fingerSwipe {
          0% {
            left: 30%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
          25% {
            left: 70%;
            top: 30%;
            transform: translate(-50%, -50%);
          }
          50% {
            left: 70%;
            top: 70%;
            transform: translate(-50%, -50%);
          }
          75% {
            left: 30%;
            top: 70%;
            transform: translate(-50%, -50%);
          }
          100% {
            left: 30%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        }

        @keyframes mousePress {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .tutorial-container {
            max-width: 90vw;
            margin: 0 16px;
          }

          .tutorial-header {
            padding: 16px 12px 10px;
          }

          .tutorial-title {
            font-size: 18px;
          }

          .tutorial-skip {
            font-size: 12px;
            padding: 6px 10px;
          }

          .tutorial-content {
            padding: 20px 12px;
            min-height: 150px;
          }

          .tutorial-content p {
            font-size: 14px;
            line-height: 1.5;
            margin: 0 0 16px;
          }

          .tutorial-footer {
            padding: 10px 12px 16px;
          }

          .progress-text {
            font-size: 12px;
          }

          .tutorial-button {
            padding: 10px 20px;
            font-size: 14px;
          }

          .joystick-base {
            width: 60px;
            height: 60px;
          }

          .joystick-knob {
            width: 24px;
            height: 24px;
          }

          .key {
            width: 32px;
            height: 32px;
            font-size: 14px;
          }

          .keyboard-demo {
            gap: 6px;
          }
        }
      `}</style>
    </div>
  );
}

export default TutorialOverlay;